import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ComplianceCard from './components/ComplianceCard';
import { complianceDashboardService } from '../../services/complianceDashboardService';
import { companyService } from '../../services/companyService';
import { siteService } from '../../services/siteService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

// ─── Card definitions ─────────────────────────────────────────────────────────
// statusColor is the colour shown when count > 0 (alert state).
// When count === 0 the card automatically shows green / OK.
const CARD_DEFINITIONS = [
  {
    key: 'expiringCerts',
    icon: 'Award',
    label: 'Certifications Expiring (30 days)',
    statusColor: 'amber',
    navigateTo: '/worker-certifications?filter=expiring',
  },
  {
    key: 'expiredCerts',
    icon: 'ShieldAlert',
    label: 'Expired Certifications',
    statusColor: 'red',
    navigateTo: '/worker-certifications?filter=expired',
  },
  {
    key: 'openIncidents',
    icon: 'AlertOctagon',
    label: 'Open Incidents',
    statusColor: 'red',
    navigateTo: '/incident-reporting?status=open',
  },
  {
    key: 'investigatingIncidents',
    icon: 'Search',
    label: 'Incidents Under Investigation',
    statusColor: 'amber',
    navigateTo: '/incident-reporting?status=investigating',
  },
  {
    key: 'unacknowledgedRams',
    icon: 'FileCheck',
    label: 'Unacknowledged H&S Docs',
    statusColor: 'red',
    navigateTo: '/rams-management?filter=unacknowledged',
  },
  {
    key: 'pendingInductions',
    icon: 'GraduationCap',
    label: 'Pending Site Inductions',
    statusColor: 'amber',
    navigateTo: '/site-inductions-management?filter=pending',
  },
  {
    key: 'unconfirmedTalks',
    icon: 'MessageSquare',
    label: 'Unconfirmed Toolbox Talks',
    statusColor: 'amber',
    navigateTo: '/toolbox-talks-management?filter=unconfirmed',
  },
  {
    key: 'incompleteTasks',
    icon: 'ClipboardList',
    label: 'Tasks Awaiting Completion',
    statusColor: 'amber',
    navigateTo: '/task-planner?filter=incomplete',
  },
  {
    key: 'openNearMisses',
    icon: 'AlertTriangle',
    label: 'Open Near Misses',
    statusColor: 'red',
    navigateTo: '/near-misses?status=open',
  },
];

const ComplianceDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();

  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [siteIds, setSiteIds] = useState([]);

  // ─── Load company + site context ────────────────────────────────────────────
  const loadContext = useCallback(async () => {
    try {
      const { data: company, error: companyError } = await companyService?.getUserCompany();
      if (companyError || !company) {
        setError('Unable to load company information.');
        setLoading(false);
        return;
      }

      setCompanyId(company?.id);

      // Site managers: fetch only their assigned sites
      // Admins: fetch all company sites
      const { data: sites, error: sitesError } = await siteService?.getAllSites();
      if (!sitesError && sites?.length) {
        setSiteIds(sites?.map(s => s?.id));
      }

      return company?.id;
    } catch (err) {
      setError('Failed to load context.');
      setLoading(false);
      return null;
    }
  }, []);

  // ─── Fetch all compliance counts ─────────────────────────────────────────────
  const fetchCounts = useCallback(async (cId, sIds) => {
    if (!cId) return;
    setLoading(true);
    setError('');

    try {
      const [
        expiringCerts,
        expiredCerts,
        openIncidents,
        investigatingIncidents,
        unacknowledgedRams,
        pendingInductions,
        unconfirmedTalks,
        incompleteTasks,
        openNearMisses,
      ] = await Promise.all([
        complianceDashboardService?.getExpiringCertifications(cId, sIds),
        complianceDashboardService?.getExpiredCertifications(cId, sIds),
        complianceDashboardService?.getOpenIncidents(cId, sIds),
        complianceDashboardService?.getInvestigatingIncidents(cId, sIds),
        complianceDashboardService?.getUnacknowledgedRams(cId, sIds),
        complianceDashboardService?.getPendingInductions(cId, sIds),
        complianceDashboardService?.getUnconfirmedTalks(cId, sIds),
        complianceDashboardService?.getIncompleteTasks(cId, sIds),
        complianceDashboardService?.getOpenNearMisses(cId),
      ]);

      setCounts({
        expiringCerts: expiringCerts?.count ?? 0,
        expiredCerts: expiredCerts?.count ?? 0,
        openIncidents: openIncidents?.count ?? 0,
        investigatingIncidents: investigatingIncidents?.count ?? 0,
        unacknowledgedRams: unacknowledgedRams?.count ?? 0,
        pendingInductions: pendingInductions?.count ?? 0,
        unconfirmedTalks: unconfirmedTalks?.count ?? 0,
        incompleteTasks: incompleteTasks?.count ?? 0,
        openNearMisses: openNearMisses?.count ?? 0,
      });

      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load compliance data. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const cId = await loadContext();
      if (cId) {
        // siteIds may not be set yet from loadContext — re-read from state after
        // context loads. We use a local variable to avoid stale closure.
        const { data: sites } = await siteService?.getAllSites();
        const ids = sites?.map(s => s?.id) || [];
        setSiteIds(ids);
        await fetchCounts(cId, ids);
      }
    };
    init();
  }, [loadContext, fetchCounts]);

  const handleRefresh = () => {
    if (companyId) fetchCounts(companyId, siteIds);
  };

  const formatLastUpdated = (date) => {
    if (!date) return '';
    return date?.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const totalAlerts = Object.values(counts)?.reduce((sum, v) => sum + (v || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Compliance Overview | SiteProof</title>
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 pt-24 pb-8 max-w-7xl">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="ArrowLeft" size={18} />
              </button>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading">
                {t('complianceOverview')}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground ml-9">
              {userProfile?.role === 'site_manager' ? t('complianceSubtitleSiteManager') : t('complianceSubtitle')}
            </p>
          </div>

          <div className="flex items-center gap-3 ml-9 sm:ml-0">
            {lastUpdated && (
              <span className="text-xs text-muted-foreground">
                Updated {formatLastUpdated(lastUpdated)}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin' : ''} />
              {t('refresh')}
            </Button>
          </div>
        </div>

        {/* Summary banner */}
        {!loading && (
          <div className={`rounded-xl border p-4 mb-6 flex items-center gap-4 ${
            totalAlerts === 0
              ? 'bg-emerald-50 border-emerald-200'
              : totalAlerts <= 5
              ? 'bg-amber-50 border-amber-200' :'bg-red-50 border-red-200'
          }`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              totalAlerts === 0 ? 'bg-emerald-100' : totalAlerts <= 5 ? 'bg-amber-100' : 'bg-red-100'
            }`}>
              <Icon
                name={totalAlerts === 0 ? 'CheckCircle2' : 'AlertTriangle'}
                size={20}
                className={totalAlerts === 0 ? 'text-emerald-600' : totalAlerts <= 5 ? 'text-amber-600' : 'text-red-600'}
              />
            </div>
            <div>
              <p className={`font-semibold text-sm ${
                totalAlerts === 0 ? 'text-emerald-800' : totalAlerts <= 5 ? 'text-amber-800' : 'text-red-800'
              }`}>
                {totalAlerts === 0
                  ? t('allComplianceClear')
                  : `${totalAlerts} compliance item${totalAlerts === 1 ? '' : 's'} require${totalAlerts === 1 ? 's' : ''} attention`}
              </p>
              <p className={`text-xs mt-0.5 ${
                totalAlerts === 0 ? 'text-emerald-600' : totalAlerts <= 5 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {totalAlerts === 0
                  ? t('noOutstandingActions') : t('clickCardToResolve')}
              </p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* 8-card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CARD_DEFINITIONS?.map(card => (
            <ComplianceCard
              key={card?.key}
              icon={card?.icon}
              label={card?.label}
              count={counts?.[card?.key] ?? 0}
              statusColor={card?.statusColor}
              navigateTo={card?.navigateTo}
              loading={loading}
            />
          ))}
        </div>

        {/* Footer note */}
        <p className="text-xs text-muted-foreground text-center mt-8">
          {t('complianceFooter')}
        </p>
      </main>
    </div>
  );
};

export default ComplianceDashboard;
